import { Controller } from '@hotwired/stimulus'

// The scoped management dashboard for one (vignette, concern, expected outcome)
// group of variations.
//
// Every variable the group touches is rendered twice by the ERB: once as a chip
// in the shared strip and once as a table column. Exactly one of the two is
// visible and enabled at a time, and switching between them is a class and
// `disabled` toggle rather than DOM surgery. That is what makes shared ⇄ varying
// reversible without losing typed values, and it is also what makes the save
// work: the server rebuilds each row's variables as `shared.merge(row)`, so a
// variable that is shared submits only its strip input, one that varies submits
// only its row inputs, and one the admin removed submits neither and disappears.
//
// Row identity travels in the field names (`group[variations][<i>][...]`), so
// added rows just need an index no rendered row is using.
export default class extends Controller {
  static targets = [
    'row',
    'rowsBody',
    'rowTemplate',
    'status',
    'statusDot',
    'statusText',
    'revertHint',
    'sharedCount',
    'sharedEmpty',
    'varyingCount',
    'noColumnsNote',
    'cardHeader',
    'selectionBar',
    'selectionCount',
    'selectionLocked',
    'deleteLabel',
    'selectAll',
    'rowCheckbox',
    'modal',
    'modalTitle',
    'modalList',
    'modalSkipped',
    'modalConfirmLabel',
    'modalIds',
    'menu'
  ]

  static values = {
    variableStates: Object,
    groupName: String
  }

  connect() {
    this.states = { ...this.variableStatesValue }
    this.nextIndex = this.rowTargets.length
    this.lastCheckedIndex = null
    this.dirty = false

    this.applyVariableStates()
    this.focusRowElement(this.selectableRows[0] || this.rowTargets[0])
    this.refreshSelection()
  }

  // --- row focus ------------------------------------------------------------

  focusRow(event) {
    const row = event.target.closest('[data-variation-group-target~="row"]')
    if (row) this.focusRowElement(row)
  }

  focusRowElement(row) {
    this.focused = row

    this.rowTargets.forEach((candidate) => {
      candidate.classList.toggle('bg-indigo-50', candidate === row)
    })

    this.publishPreview()
  }

  publishPreview() {
    const row = this.focused
    const values = { ...this.sharedValues() }

    if (row) {
      Object.assign(values, this.rowValues(row))
    }

    this.dispatch('focus', {
      target: document,
      detail: {
        label: row ? this.versionInput(row).value.trim() : '',
        values,
        sharedKeys: this.keysWithState('shared')
      }
    })
  }

  // --- editing --------------------------------------------------------------

  markDirty() {
    if (this.dirty) return
    this.dirty = true

    this.statusDotTarget.classList.remove('bg-[#059669]')
    this.statusDotTarget.classList.add('bg-amber-500')
    this.statusTextTarget.classList.remove('text-green-600')
    this.statusTextTarget.classList.add('text-amber-600')
    this.statusTextTarget.textContent = 'Unsaved changes'

    // The status turns into the way out: revert without leaving the group.
    this.revertHintTarget.classList.remove('hidden')
    this.statusTarget.classList.replace('cursor-default', 'cursor-pointer')
    this.statusTarget.classList.add('hover:bg-amber-50')
    this.statusTarget.title =
      'Discard unsaved changes and reload the last saved state'
  }

  // Reverting reloads the group rather than replaying edits in reverse: the last
  // saved state is whatever is in the database, and re-fetching it can't drift
  // from that the way an undo stack could.
  revert(event) {
    event.preventDefault()
    if (!this.dirty) return

    const confirmed = window.confirm(
      'Discard your unsaved changes? This group will go back to the last saved state.'
    )
    if (!confirmed) return

    this.dirty = false
    // `replace`, not `reload`: a reload lets the browser restore the form fields
    // it just cached, which is precisely what the admin asked to throw away.
    window.location.replace(window.location.href)
  }

  // Every keystroke and pick lands here: refresh the preview, flag the page as
  // dirty, and let the value become a suggestion for the same variable elsewhere.
  valueChanged(event) {
    // Row checkboxes also raise `input`; selecting a row isn't an edit.
    if (event.target.type === 'checkbox') return

    const row = event.target.closest('[data-variation-group-target~="row"]')
    if (row && row !== this.focused) this.focusRowElement(row)

    this.markDirty()
    this.shareSuggestion(event.target)
    this.syncSharedChip(event.target)
    this.publishPreview()
  }

  // A value typed in one cell should be offered by every other combobox for the
  // same variable, without a round trip.
  shareSuggestion(input) {
    const name = input.dataset.variableName
    const value = input.value.trim()
    if (!name || value === '') return

    this.element
      .querySelectorAll(
        `[data-controller~="combobox"][data-combobox-variable="${CSS.escape(name)}"]`
      )
      .forEach((combobox) => {
        if (combobox.contains(input)) return

        const current = JSON.parse(
          combobox.dataset.comboboxSuggestionsValue || '[]'
        )
        if (
          current.some((entry) => entry.toLowerCase() === value.toLowerCase())
        )
          return

        combobox.dataset.comboboxSuggestionsValue = JSON.stringify([
          value,
          ...current
        ])
      })
  }

  // Keeps a shared chip's read-only face in step with its editable one.
  syncSharedChip(input) {
    const chip = input.closest('[data-variable-chip]')
    if (!chip) return

    const display = chip.querySelector('[data-shared-display-value]')
    if (display) display.textContent = input.value.trim() || '—'
  }

  // --- rows -----------------------------------------------------------------

  addVariation(event) {
    event.preventDefault()
    this.appendRow(this.focused)
  }

  duplicateRow(event) {
    event.preventDefault()
    this.closeMenus()
    this.appendRow(event.target.closest('[data-variation-group-target~="row"]'))
  }

  duplicateSelected(event) {
    event.preventDefault()
    this.selectedRows.forEach((row) => this.appendRow(row))
    this.clearSelection(event)
  }

  // A new row starts as a copy of the row the admin was looking at, so the only
  // thing left to do is change the one cell that differs.
  appendRow(sourceRow) {
    const index = this.nextIndex++
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    const row = fragment.querySelector('tr')

    row.innerHTML = row.innerHTML.replaceAll('__INDEX__', String(index))
    this.rowsBodyTarget.appendChild(row)

    if (sourceRow) {
      row.querySelectorAll('input[data-variable-name]').forEach((input) => {
        const source = sourceRow.querySelector(
          `input[data-variable-name="${CSS.escape(input.dataset.variableName)}"]`
        )
        if (source) input.value = source.value
      })
    }

    this.versionInput(row).value = this.suggestedVersion()

    this.applyVariableStates()
    this.markDirty()
    this.focusRowElement(row)
    this.refreshSelection()
  }

  removeRow(event) {
    event.preventDefault()
    this.closeMenus()

    const row = event.target.closest('[data-variation-group-target~="row"]')
    if (!row || row.dataset.locked === 'true') return

    if (row.dataset.new === 'true') {
      // Never saved, so there is nothing for the server to delete.
      row.remove()
    } else {
      row.querySelector('[data-row-destroy]').value = '1'
      row.classList.add('hidden')
    }

    this.markDirty()
    if (this.focused === row) this.focusRowElement(this.visibleRows[0])
    this.refreshSelection()
  }

  // Suggests the next `v<n>` label, which the admin is free to overwrite or
  // clear — `version` is a free-text label, not a sequence.
  suggestedVersion() {
    const numbers = this.rowTargets
      .map((row) =>
        this.versionInput(row)
          .value.trim()
          .match(/^v(\d+)$/i)
      )
      .filter(Boolean)
      .map((match) => parseInt(match[1], 10))

    return `v${(numbers.length > 0 ? Math.max(...numbers) : 0) + 1}`
  }

  // --- shared ⇄ varying -----------------------------------------------------

  editSharedValues(event) {
    event.preventDefault()
    this.element
      .querySelectorAll('[data-variable-chip]')
      .forEach((chip) => this.setChipEditing(chip, true))
  }

  editSharedChip(event) {
    this.setChipEditing(event.target.closest('[data-variable-chip]'), true)
  }

  setChipEditing(chip, editing) {
    if (!chip) return

    chip
      .querySelector('[data-shared-display]')
      ?.classList.toggle('hidden', editing)
    chip
      .querySelector('[data-shared-edit]')
      ?.classList.toggle('hidden', !editing)
  }

  // Moving a variable into the strip needs one winning value. If the rows
  // already agree there is nothing to ask; if they don't, the admin picks.
  makeShared(event) {
    event.preventDefault()

    const name = event.currentTarget.dataset.variableName
    const values = [
      ...new Set(
        this.visibleRows
          .map((row) => this.rowInput(row, name)?.value.trim())
          .filter((value) => value)
      )
    ]

    if (values.length > 1) {
      this.openValueChooser(event.currentTarget, name, values)
      return
    }

    this.applyShared(name, values[0] || this.sharedInput(name)?.value || '')
  }

  chooseSharedValue(event) {
    event.preventDefault()
    this.applyShared(
      event.currentTarget.dataset.variableName,
      event.currentTarget.dataset.value
    )
  }

  applyShared(name, value) {
    const input = this.sharedInput(name)
    if (input) {
      input.value = value
      this.syncSharedChip(input)
    }

    this.states[name] = 'shared'
    this.closeMenus()
    this.applyVariableStates()
    this.markDirty()
    this.publishPreview()
  }

  makeVarying(event) {
    event.preventDefault()

    const name = event.currentTarget.dataset.variableName
    const shared = this.sharedInput(name)?.value || ''

    // Seed the new column from the shared value so promoting a variable doesn't
    // silently blank it on every row.
    this.rowTargets.forEach((row) => {
      const input = this.rowInput(row, name)
      if (input && input.value.trim() === '') input.value = shared
    })

    this.states[name] = 'varying'
    this.closeMenus()
    this.applyVariableStates()
    this.markDirty()
    this.publishPreview()
  }

  removeColumn(event) {
    event.preventDefault()

    this.states[event.currentTarget.dataset.variableName] = 'removed'
    this.closeMenus()
    this.applyVariableStates()
    this.markDirty()
    this.publishPreview()
  }

  // Reflects `this.states` onto the DOM. A disabled field is not submitted, which
  // is exactly how a variable ends up shared, varying, or gone.
  applyVariableStates() {
    Object.entries(this.states).forEach(([name, state]) => {
      const selector = CSS.escape(name)

      this.element
        .querySelectorAll(`[data-variable-chip="${selector}"]`)
        .forEach((chip) => {
          chip.classList.toggle('hidden', state !== 'shared')
          this.setFieldsEnabled(chip, state === 'shared')
          if (state === 'shared') this.setChipEditing(chip, false)
        })

      this.element
        .querySelectorAll(`[data-variable-column="${selector}"]`)
        .forEach((cell) => {
          cell.classList.toggle('hidden', state !== 'varying')
          this.setFieldsEnabled(cell, state === 'varying')
        })

      this.element
        .querySelectorAll(`[data-variable-menu-item="${selector}"]`)
        .forEach((item) => {
          item.classList.toggle('hidden', item.dataset.menuState === state)
        })
    })

    this.refreshCounts()
  }

  setFieldsEnabled(container, enabled) {
    container
      .querySelectorAll('input, select, textarea')
      .forEach((field) => (field.disabled = !enabled))
  }

  refreshCounts() {
    const shared = this.keysWithState('shared')
    const varying = this.keysWithState('varying')

    if (this.hasSharedCountTarget) {
      this.sharedCountTarget.textContent = `Shared across all ${this.visibleRows.length} variations in this group`
    }
    if (this.hasSharedEmptyTarget) {
      this.sharedEmptyTarget.classList.toggle('hidden', shared.length > 0)
    }
    if (this.hasVaryingCountTarget) {
      this.varyingCountTarget.textContent = `· ${varying.length} ${
        varying.length === 1 ? 'column' : 'columns'
      } differ across rows`
    }
    if (this.hasNoColumnsNoteTarget) {
      this.noColumnsNoteTarget.classList.toggle('hidden', varying.length > 0)
    }
  }

  // --- selection ------------------------------------------------------------

  toggleSelectAll() {
    const checked = this.selectAllTarget.checked
    this.selectableRows.forEach((row) => {
      this.checkbox(row).checked = checked
    })
    this.refreshSelection()
  }

  toggleRowSelection(event) {
    const boxes = this.selectableRows.map((row) => this.checkbox(row))
    const index = boxes.indexOf(event.target)

    // Shift-click extends from the last box the admin clicked.
    if (event.shiftKey && this.lastCheckedIndex !== null && index > -1) {
      const [from, to] = [this.lastCheckedIndex, index].sort((a, b) => a - b)
      boxes
        .slice(from, to + 1)
        .forEach((box) => (box.checked = event.target.checked))
    }

    this.lastCheckedIndex = index
    this.refreshSelection()
  }

  clearSelection(event) {
    event?.preventDefault()
    this.selectableRows.forEach((row) => (this.checkbox(row).checked = false))
    this.lastCheckedIndex = null
    this.refreshSelection()
  }

  refreshSelection() {
    const selected = this.selectedRows
    const selectable = this.selectableRows

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked =
        selectable.length > 0 && selected.length === selectable.length
      this.selectAllTarget.indeterminate =
        selected.length > 0 && selected.length < selectable.length
      this.selectAllTarget.disabled = selectable.length === 0
    }

    if (this.hasSelectionBarTarget) {
      this.selectionBarTarget.classList.toggle('hidden', selected.length === 0)
      this.cardHeaderTarget.classList.toggle('hidden', selected.length > 0)
      this.selectionCountTarget.textContent = `${selected.length} ${
        selected.length === 1 ? 'variation' : 'variations'
      } selected`
      this.deleteLabelTarget.textContent = `Delete ${selected.length} ${
        selected.length === 1 ? 'variation' : 'variations'
      }`
      this.selectionLockedTarget.textContent = this.lockedNote()
    }

    this.refreshCounts()
  }

  lockedNote() {
    const labels = this.lockedRows.map((row) => this.rowLabel(row))
    if (labels.length === 0) return ''

    const shown = labels.slice(0, 3).join(', ')
    const extra = labels.length > 3 ? ` +${labels.length - 3} more` : ''
    return `${shown}${extra} can't be selected — used in a reliability test`
  }

  // --- bulk delete ----------------------------------------------------------

  openDeleteModal(event) {
    event.preventDefault()

    const selected = this.selectedRows
    if (selected.length === 0) return

    this.modalTitleTarget.textContent = `Delete ${selected.length} ${
      selected.length === 1 ? 'variation' : 'variations'
    }?`
    this.modalConfirmLabelTarget.textContent = `Delete ${selected.length} ${
      selected.length === 1 ? 'variation' : 'variations'
    }`

    this.modalListTarget.replaceChildren(
      ...selected.map((row, index) =>
        this.buildModalRow(row, index === selected.length - 1)
      )
    )
    this.modalIdsTarget.replaceChildren(
      ...selected.map((row) => {
        const field = document.createElement('input')
        field.type = 'hidden'
        field.name = 'variation_ids[]'
        field.value = row.dataset.rowId
        return field
      })
    )

    const locked = this.lockedRows.map((row) => this.rowLabel(row))
    this.modalSkippedTarget.classList.toggle('hidden', locked.length === 0)
    this.modalSkippedTarget.textContent =
      locked.length > 0
        ? `${locked.join(', ')} ${
            locked.length === 1 ? 'was' : 'were'
          } skipped — used in a reliability test.`
        : ''

    this.modalTarget.classList.remove('hidden')
  }

  closeDeleteModal(event) {
    event?.preventDefault()
    this.modalTarget.classList.add('hidden')
  }

  buildModalRow(row, isLast) {
    const item = document.createElement('div')
    item.className = `flex items-center gap-3 px-3 py-2 ${isLast ? '' : 'border-b border-gray-100'}`

    const badge = document.createElement('span')
    const label = this.versionInput(row).value.trim()
    badge.textContent = label || 'unlabeled'
    badge.className = label
      ? 'flex-none rounded-md border border-gray-200 bg-gray-100 px-2 py-[3px] font-mono text-[11.5px] font-semibold text-gray-900'
      : 'flex-none rounded-md border border-gray-200 bg-gray-100 px-2 py-[3px] font-mono text-[11.5px] font-semibold text-gray-400'

    const descriptor = document.createElement('span')
    descriptor.className = 'truncate text-[12.5px] text-gray-600'
    descriptor.textContent =
      this.rowDescriptor(row) || 'No distinguishing values'

    item.append(badge, descriptor)
    return item
  }

  rowDescriptor(row) {
    return this.keysWithState('varying')
      .map((name) => this.rowInput(row, name)?.value.trim())
      .filter((value) => value)
      .join(' · ')
  }

  // --- menus ----------------------------------------------------------------

  toggleMenu(event) {
    event.preventDefault()

    const panel = event.currentTarget.nextElementSibling
    const wasHidden = panel.classList.contains('hidden')
    this.closeMenus()
    panel.classList.toggle('hidden', !wasHidden)
  }

  closeMenusOutside(event) {
    if (event.target.closest('[data-variation-group-target~="menu"]')) return
    if (event.target.closest('[data-menu-toggle]')) return

    this.closeMenus()
  }

  closeMenus() {
    this.menuTargets.forEach((menu) => {
      menu.classList.add('hidden')
      menu.querySelector('[data-value-chooser]')?.classList.add('hidden')
      menu.querySelector('[data-menu-body]')?.classList.remove('hidden')
    })
  }

  // Shown when rows disagree about a value that is being made shared.
  openValueChooser(trigger, name, values) {
    const menu = trigger.closest('[data-variation-group-target~="menu"]')
    if (!menu) {
      this.applyShared(name, values[0])
      return
    }

    const chooser = menu.querySelector('[data-value-chooser]')
    const list = menu.querySelector('[data-value-chooser-list]')

    list.replaceChildren(
      ...values.map((value) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.textContent = value
        button.dataset.variableName = name
        button.dataset.value = value
        button.setAttribute(
          'data-action',
          'click->variation-group#chooseSharedValue'
        )
        button.className =
          'block w-full truncate rounded-md px-[9px] py-[6px] text-left text-[12.5px] font-medium text-gray-900 hover:bg-indigo-50'
        return button
      })
    )

    menu.querySelector('[data-menu-body]')?.classList.add('hidden')
    chooser.classList.remove('hidden')
  }

  // --- lookups --------------------------------------------------------------

  keysWithState(state) {
    return Object.entries(this.states)
      .filter(([, value]) => value === state)
      .map(([name]) => name)
  }

  sharedValues() {
    return this.keysWithState('shared').reduce((values, name) => {
      values[name] = this.sharedInput(name)?.value.trim() || ''
      return values
    }, {})
  }

  rowValues(row) {
    return this.keysWithState('varying').reduce((values, name) => {
      values[name] = this.rowInput(row, name)?.value.trim() || ''
      return values
    }, {})
  }

  sharedInput(name) {
    return this.element.querySelector(
      `[data-variable-chip="${CSS.escape(name)}"] input[data-variable-name]`
    )
  }

  rowInput(row, name) {
    return row.querySelector(`input[data-variable-name="${CSS.escape(name)}"]`)
  }

  versionInput(row) {
    return row.querySelector('[data-version-input]')
  }

  rowLabel(row) {
    return this.versionInput(row).value.trim() || 'unlabeled'
  }

  checkbox(row) {
    return row.querySelector('[data-variation-group-target~="rowCheckbox"]')
  }

  get visibleRows() {
    return this.rowTargets.filter((row) => !row.classList.contains('hidden'))
  }

  get selectableRows() {
    return this.visibleRows.filter(
      (row) => this.checkbox(row) && !this.checkbox(row).disabled
    )
  }

  get selectedRows() {
    return this.selectableRows.filter((row) => this.checkbox(row).checked)
  }

  get lockedRows() {
    return this.visibleRows.filter((row) => row.dataset.locked === 'true')
  }
}
